// src/pages/CustomerForm.jsx
import { useState, useEffect } from "react";
import {
  TextInput,
  Button,
  Text,
  Title,
  Paper,
  Stack,
  Group,
  FileButton,
  Image,
  Divider,
  Badge,
  Box,
} from "@mantine/core";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBooking } from "../context/BookingContext";
import imageCompression from "browser-image-compression";
import HomePage from "./HomePage";

export default function CustomerForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { bookingData, setBookingData } = useBooking();
  const shopKey = searchParams.get("shopKey");
  const isValidShopKey = shopKey;
  const [processingImage, setProcessingImage] = useState(false);

  const customer = bookingData.customer;

  const [form, setForm] = useState({
    name: customer?.name || "",
    mobile: customer?.mobile || "",
    email: customer?.email || "",
  });

  const [dlImage, setDlImage] = useState(customer?.dlDocument || null);
  const [aadhaarImage, setAadhaarImage] = useState(customer?.aadhaarDocument || null);

  const [dlPreview, setDlPreview] = useState(null);
  const [aadhaarPreview, setAadhaarPreview] = useState(null);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setErrors({ ...errors, [field]: "" });
  };

  useEffect(() => {
    const customer = bookingData.customer;

    setForm({
      name: customer?.name || "",
      mobile: customer?.mobile || "",
      email: customer?.email || "",
    });

    setDlImage(customer?.dlDocument || null);
    setAadhaarImage(customer?.aadhaarDocument || null);
  }, [bookingData.customer]);


  useEffect(() => {
    if (dlImage && !dlPreview) {
      setDlPreview(URL.createObjectURL(dlImage));
    }

    if (aadhaarImage && !aadhaarPreview) {
      setAadhaarPreview(URL.createObjectURL(aadhaarImage));
    }
  }, [dlImage, aadhaarImage]);

  const handleFileSelect = async (file, type) => {
    if (!file) return;

    setProcessingImage(true);

    const instantPreview = URL.createObjectURL(file);

    if (type === "dl") {
      setDlPreview(instantPreview);
    } else {
      setAadhaarPreview(instantPreview);
    }

    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1000,
        useWebWorker: true,
        initialQuality: 0.7,
      });

      if (type === "dl") {
        setDlImage(compressedFile);
      } else {
        setAadhaarImage(compressedFile);
      }

    } catch (err) {
      console.log(err);
    } finally {
      setProcessingImage(false);
    }
  };
  const validate = () => {
    const newErrors = {};

    // Name
    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    } else if (form.name.length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(form.name)) {
      newErrors.name = "Name should contain only letters";
    }

    // Mobile
    if (!form.mobile.trim()) {
      newErrors.mobile = "Mobile is required";
    } else if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      newErrors.mobile = "Enter valid Indian mobile number";
    }

    // Email (optional but validate if present)
    // if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    //   newErrors.email = "Enter valid email address";
    // }

    // Driving License
    if (!dlImage) {
      newErrors.dl = "Driving Licence is required";
    } else if (!dlImage.type.startsWith("image/")) {
      newErrors.dl = "Only image files allowed";
    } else if (dlImage.size > 4 * 1024 * 1024) {
      newErrors.dl = "Max size 4MB";
    }


    // if (!aadhaarImage) {
    //   newErrors.aadhaar = "Aadhaar Card is required";
    // } else if (!aadhaarImage.type.startsWith("image/")) {
    //   newErrors.aadhaar = "Only image files allowed";
    // } else if (aadhaarImage.size > 4 * 1024 * 1024) {
    //   newErrors.aadhaar = "Max size 4MB";
    // }
    


    return newErrors;
  };
  // REPLACE ONLY handleNext FUNCTION

  const handleNext = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      // ✅ SAVE TO CONTEXT
      setBookingData(prev => ({
        ...prev,
        shopKey: shopKey,
        customer: {
          ...form,
          dlDocument: dlImage,
          aadhaarDocument: aadhaarImage,
        },
      }));

      // ✅ NO NEED TO PASS STATEret
      navigate("/booking");
    } catch (err) {
      console.log("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isValidShopKey) {

    return (
      <HomePage />
    );
  }

  return (
    <Box
      style={{
        minHeight: "100vh",
        backgroundColor: "#f0ede8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Box style={{ width: "100%", maxWidth: 480 }}>

        {/* Header */}
        <Stack align="center" mb={32} gap={4}>
          <Box
            style={{
              width: 60,
              height: 60,
              borderRadius: 12,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
            }}
          >
            <img
              src="/rentalflow.png"
              alt="RentalFlow"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Title order={2} fw={800} c="#1a1a1a">Book a Vehicle</Title>
          <Text c="#888" size="sm">Fill in your details to proceed</Text>
        </Stack>

        {/* Progress */}
        <Group mb={24} gap={8}>
          <Box style={{ flex: 1, height: 4, backgroundColor: "#f24c1a", borderRadius: 4 }} />
          <Box style={{ flex: 1, height: 4, backgroundColor: "#e0ddd8", borderRadius: 4 }} />
          <Text size="xs" c="#888">Step 1 of 2</Text>
        </Group>

        <Paper radius={18} p={24} shadow="xs" bg="white">
          <Title order={4} fw={800} mb={20} c="#1a1a1a">Personal Details</Title>

          <Stack gap={16}>
            {/* Name */}
            <TextInput
              label="Full Name"
              placeholder="Enter your full name"
              value={form.name}
              onChange={handleChange("name")}
              error={errors.name}
              required
              styles={{
                input: {
                  borderRadius: 10,
                  borderColor: errors.name ? "#e53e3e" : "#e0ddd8",
                  backgroundColor: "#f5f2ed",
                  border: "none",
                  "&:focus": { borderColor: "#f24c1a" },
                },
                label: { fontWeight: 600, fontSize: 12, color: "#555" },
              }}
            />

            {/* Mobile */}
            <TextInput
              label="Mobile Number"
              placeholder="10-digit mobile number"
              value={form.mobile}
              onChange={handleChange("mobile")}
              error={errors.mobile}
              required
              maxLength={10}
              styles={{
                input: {
                  borderRadius: 10,
                  backgroundColor: "#f5f2ed",
                  border: "none",
                },
                label: { fontWeight: 600, fontSize: 12, color: "#555" },
              }}
            />

            {/* <TextInput
              label="Email (Optional)"
              placeholder="your@email.com"
              value={form.email}
              onChange={handleChange("email")}
              error={errors.email}
              
              styles={{
                input: {
                  borderRadius: 10,
                  backgroundColor: "#f5f2ed",
                  border: "none",
                },
                label: { fontWeight: 600, fontSize: 12, color: "#555" },
              }}
            /> */}

          </Stack>
        </Paper>

        {/* Documents */}
        <Paper radius={18} p={24} shadow="xs" bg="white" mt={16}>
          <Title order={4} fw={800} mb={4} c="#1a1a1a">Documents</Title>
          <Text size="xs" c="#888" mb={20}>
            Upload clear photos of your documents less than 4MB
          </Text>

          <Stack gap={16}>

            {/* Driving Licence */}
            <Box>
              <Group justify="space-between" mb={8}>
                <Text size="sm" fw={600} c="#555">Driving Licence</Text>
                <Badge color={dlImage ? "green" : "red"} variant="light" size="sm">
                  {dlImage ? "✓ Uploaded" : "Required"}
                </Badge>
              </Group>

              {dlPreview ? (
                <Box style={{ position: "relative" }}>
                  <Image
                    src={dlPreview}
                    radius={12}
                    height={140}
                    fit="cover"
                  />
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    mt={8}
                    onClick={() => { setDlImage(null); setDlPreview(null); }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Group gap={10}>
                  <FileButton
                    onChange={(file) => handleFileSelect(file, "dl")}
                    accept="image/*"
                    capture="environment"
                  >
                    {(props) => (
                      <Button
                        {...props}
                        variant="light"
                        color="orange"
                        radius={10}
                        style={{ flex: 1 }}
                        leftSection={<Text>📷</Text>}
                      >
                        Camera
                      </Button>
                    )}
                  </FileButton>
                  <FileButton
                    onChange={(file) => handleFileSelect(file, "dl")}
                    accept="image/*"
                  >
                    {(props) => (
                      <Button
                        {...props}
                        variant="light"
                        color="gray"
                        radius={10}
                        style={{ flex: 1 }}
                        leftSection={<Text>🖼️</Text>}
                      >
                        Gallery
                      </Button>
                    )}
                  </FileButton>
                </Group>
              )}
              {errors.dl && <Text size="xs" c="red" mt={4}>{errors.dl}</Text>}
            </Box>

            <Divider />

            {/* Aadhaar Card */}
            <Box>
              <Group justify="space-between" mb={8}>
                <Text size="sm" fw={600} c="#555">Aadhaar Card</Text>
                
                {aadhaarImage ? <Badge color={aadhaarImage ? "green" : "red"} variant="light" size="sm">
                  {aadhaarImage ? "✓ Uploaded" : "Required"}
                </Badge> : null}
                
              </Group>

              {aadhaarPreview ? (
                <Box style={{ position: "relative" }}>
                  <Image
                    src={aadhaarPreview}
                    radius={12}
                    height={140}
                    fit="cover"
                  />
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    mt={8}
                    onClick={() => { setAadhaarImage(null); setAadhaarPreview(null); }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Group gap={10}>
                  <FileButton
                    onChange={(file) => handleFileSelect(file, "aadhaar")}
                    accept="image/*"
                    capture="environment"
                  >
                    {(props) => (
                      <Button
                        {...props}
                        variant="light"
                        color="orange"
                        radius={10}
                        style={{ flex: 1 }}
                        leftSection={<Text>📷</Text>}
                      >
                        Camera
                      </Button>
                    )}
                  </FileButton>
                  <FileButton
                    onChange={(file) => handleFileSelect(file, "aadhaar")}
                    accept="image/*"
                  >
                    {(props) => (
                      <Button
                        {...props}
                        variant="light"
                        color="gray"
                        radius={10}
                        style={{ flex: 1 }}
                        leftSection={<Text>🖼️</Text>}
                      >
                        Gallery
                      </Button>
                    )}
                  </FileButton>
                </Group>
              )}
              {errors.aadhaar && <Text size="xs" c="red" mt={4}>{errors.aadhaar}</Text>}
            </Box>
          </Stack>
        </Paper>

        {/* Next Button */}
        <Button
          fullWidth
          mt={20}
          h={52}
          radius={14}
          disabled={processingImage}
          loading={processingImage || loading}
          onClick={handleNext}
          style={{
            backgroundColor: "#f24c1a",
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          Continue to Booking →
        </Button>

        {/* <Text size="xs" c="#aaa" ta="center" mt={16}>
          Your data is secure and will only be shared with the shop owner
        </Text> */}

      </Box>
    </Box>
  );
}